import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface Manychat {
  _id: string;
  apiKey?: string;
}

export function SettingsManychat() {
  const [manychat, setManychat] = useState<Manychat | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingManychat, setIsCreatingManychat] = useState(false);
  const [isDeletingManychat, setIsDeletingManychat] = useState(false);
  const [isSavingManychat, setIsSavingManychat] = useState(false);
  const [showManychatApiKey, setShowManychatApiKey] = useState(false);
  const [manychatApiKey, setManychatApiKey] = useState("");

  useEffect(() => {
    async function fetchManychat() {
      setIsLoading(true);
      try {
        const manychatResponse = await fetch("/api/manychat");
        if (manychatResponse.ok) {
          const manychatData = await manychatResponse.json();
          if (manychatData.manychat) {
            setManychat(manychatData.manychat);
            setManychatApiKey(manychatData.manychat.apiKey || "");
          }
        } else if (manychatResponse.status !== 404) {
          console.error("Failed to fetch Manychat integration");
        }
      } catch (error) {
        console.error("Failed to fetch Manychat integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchManychat();
  }, []);

  const handleCreateManychat = async () => {
    if (!manychatApiKey.trim()) {
      toast.error("API key is required");
      return;
    }

    setIsCreatingManychat(true);
    try {
      const response = await fetch("/api/manychat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: manychatApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.manychat) {
          setManychat(data.manychat);
          setManychatApiKey(data.manychat.apiKey || "");
          toast.success("Manychat integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Manychat integration");
      }
    } catch (error) {
      console.error("Error creating Manychat integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Manychat integration");
    } finally {
      setIsCreatingManychat(false);
    }
  };

  const handleSaveManychat = async () => {
    if (!manychat) return;

    setIsSavingManychat(true);
    try {
      const response = await fetch("/api/manychat", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: manychatApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.manychat) {
          setManychat(data.manychat);
          toast.success("Manychat settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Manychat settings");
      }
    } catch (error) {
      console.error("Error saving Manychat settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Manychat settings");
    } finally {
      setIsSavingManychat(false);
    }
  };

  const handleDeleteManychat = async () => {
    if (!manychat) return;

    if (!confirm("Are you sure you want to delete your Manychat integration?")) {
      return;
    }

    setIsDeletingManychat(true);
    try {
      const response = await fetch(`/api/manychat/${manychat._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setManychat(null);
        setManychatApiKey("");
        toast.success("Manychat integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Manychat integration");
      }
    } catch (error) {
      console.error("Error deleting Manychat integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Manychat integration");
    } finally {
      setIsDeletingManychat(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Manychat</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Manychat account to update subscribers and apply tags in flows
            </p>
            {manychat && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {manychat && (
            <Button onClick={handleDeleteManychat} disabled={isDeletingManychat} variant="destructive">
              {isDeletingManychat ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        <div className="space-y-2 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="manychatApiKey">API Key</Label>
            <div className="relative">
              <Input
                id="manychatApiKey"
                type={showManychatApiKey ? "text" : "password"}
                value={manychatApiKey}
                onChange={(e) => setManychatApiKey(e.target.value)}
                placeholder="Enter Manychat API key"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowManychatApiKey(!showManychatApiKey)}
              >
                {showManychatApiKey ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          {!manychat ? (
            <Button onClick={handleCreateManychat} disabled={isCreatingManychat}>
              {isCreatingManychat ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          ) : (
            <Button onClick={handleSaveManychat} disabled={isSavingManychat}>
              {isSavingManychat ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
